/**
 * Helper functions for geocoding operations
 * Extracted from GeocodeData.vue for better testability and reusability
 */

/**
 * Required headers for geocoding
 */
export const REQUIRED_GEOCODE_HEADERS = [
    "address",
    "city",
    "state",
    "country",
    "latitude",
    "longitude"
];

/**
 * Validates that an Excel range has the required headers for geocoding
 *
 * @param {Array<Array>} values - The Excel range values (first row should be headers)
 * @returns {object} Validation result with { valid, missingHeaders, headers, zipHeader, latitudeIndex, longitudeIndex }
 */
export function validateGeocodeHeaders(values) {
    if (!values || values.length < 2) {
        return {
            valid: false,
            error: "Selected range has no data to process.",
            missingHeaders: [],
            headers: [],
            zipHeader: null,
            latitudeIndex: -1,
            longitudeIndex: -1
        };
    }

    // Normalize headers to lowercase and trim
    const headers = values[0].map((h) => h?.toString().toLowerCase().trim());

    // Check for zip or postalcode
    const zipHeader = headers.includes("zip") ? "zip" :
                     headers.includes("postalcode") ? "postalcode" : null;

    // Find missing required headers
    const missingHeaders = REQUIRED_GEOCODE_HEADERS.filter((h) => !headers.includes(h));

    if (!zipHeader) {
        missingHeaders.push("zip/postalcode");
    }

    // Get column indices for latitude and longitude
    const latitudeIndex = headers.indexOf("latitude");
    const longitudeIndex = headers.indexOf("longitude");

    return {
        valid: missingHeaders.length === 0,
        error: missingHeaders.length > 0 ? `Missing required headers: ${missingHeaders.join(", ")}` : null,
        missingHeaders,
        headers,
        zipHeader,
        latitudeIndex,
        longitudeIndex
    };
}

/**
 * Builds geocode data structure from Excel values
 *
 * @param {Array<Array>} values - The Excel range values
 * @param {Array<string>} headers - The normalized headers
 * @param {string} zipHeader - The name of the zip column ("zip" or "postalcode")
 * @param {number} latitudeIndex - Column index for latitude
 * @param {number} longitudeIndex - Column index for longitude
 * @returns {Array<object>} Array of address objects ready for geocoding API
 */
export function buildGeocodeData(values, headers, zipHeader, latitudeIndex, longitudeIndex) {
    const addressData = [];

    for (let i = 1; i < values.length; i++) {
        const row = values[i];

        // Skip empty rows
        if (!row || row.every((cell) => !cell)) {
            continue;
        }

        const obj = {
            // Include row and column indices in the ID for response mapping
            id: `row_${i}_${latitudeIndex}_${longitudeIndex}`,
            address: row[headers.indexOf("address")]?.toString() || "",
            city: row[headers.indexOf("city")]?.toString() || "",
            state: row[headers.indexOf("state")]?.toString() || "",
            country: row[headers.indexOf("country")]?.toString() || "",
            postalcode: row[headers.indexOf(zipHeader)]?.toString() || "",
        };

        addressData.push(obj);
    }

    return addressData;
}

/**
 * Parses a geocode result ID to extract row and column offsets
 * Expected format: "row_{rowOffset}_{latColOffset}_{lonColOffset}"
 *
 * @param {string} id - The geocode result ID
 * @returns {object} Object with rowOffset, latColOffset, lonColOffset
 */
export function parseGeocodeResultId(id) {
    const parts = id.split("_");
    return {
        rowOffset: Number(parts[1]),
        latColOffset: Number(parts[2]),
        lonColOffset: Number(parts[3])
    };
}

/**
 * Calculates absolute Excel coordinates from range start position and offsets
 *
 * @param {number} startRow - The starting row index
 * @param {number} startCol - The starting column index
 * @param {object} offsets - Object with rowOffset, latColOffset, lonColOffset
 * @returns {object} Object with absoluteRow, absoluteLatCol, absoluteLonCol
 */
export function calculateAbsoluteCoordinates(startRow, startCol, offsets) {
    return {
        absoluteRow: startRow + offsets.rowOffset,
        absoluteLatCol: startCol + offsets.latColOffset,
        absoluteLonCol: startCol + offsets.lonColOffset
    };
}
